/**
 * Props getter that makes a non-interactive element (div/p/span) fully
 * keyboard-operable: focusable, announced as a button, and activatable
 * with Enter/Space.
 *
 * Use this for composite "clickable card" elements that contain nested
 * interactive children (buttons/links), where wrapping in a real
 * <button> would produce invalid HTML. For simple single-action
 * elements, prefer a real <button> or <Link> instead.
 *
 * Usage:
 *   <div {...getClickableProps(handleOpen, { label: "Open certificate" })} />
 */
export function getClickableProps(
  onClick,
  { role = "button", disabled = false, label } = {},
) {
  return {
    role,
    tabIndex: disabled ? undefined : 0,
    "aria-label": label,
    "aria-disabled": disabled || undefined,
    onClick: disabled
      ? undefined
      : (e) => {
          if (e.target !== e.currentTarget) return;
          onClick(e);
        },
    onKeyDown: disabled
      ? undefined
      : (e) => {
          // A nested interactive element (button/link) handles its own keys;
          // acting here too would double-fire the card action.
          if (e.target !== e.currentTarget) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault(); // stop Space from scrolling the page
            onClick(e);
          }
        },
  };
}

export default getClickableProps;
